import React, { useEffect, useState } from "react";
import { TextField, Button, Box } from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import fondo_intro_jhimbo from "../../../images/fondo_intro_jhimbo.png";
import jhimbo_intro_1 from "../../../images/jhimbo_intro_1.png";
import jhimbo_intro_2 from "../../../images/jhimbo_intro_2.png";
import jhimbo_intro_3 from "../../../images/jhimbo_intro_3.png";
import { ApiService } from "../../ApiService";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useCompanyAssigned } from "../../CompanyAssignedContext";

const HomePresentacionEdit = ({ empresaDetails, keyBranch }) => {
	// Unificamos title + subtitle en un solo campo
	const [editedTexts, setEditedTexts] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const { nit } = useParams();

	const { companyAssigned = [] } = useCompanyAssigned() || [];

	useEffect(() => {
		if (empresaDetails) {
			setEditedTexts(empresaDetails);
		}
	}, [empresaDetails, keyBranch]);

	const [editingField, setEditingField] = useState(null);
	const [tempText, setTempText] = useState("");

	// -----------------------------
	//   1. Manejo de edición
	// -----------------------------
	const handleEdit = (field) => {
		// Si se hace clic en un campo diferente, cerramos el actual
		if (editingField && editingField !== field) {
			setEditingField(null);
		}
		setTempText(editedTexts[field]);
		setEditingField(field);
	};

	const handleSave = async () => {
		if (!keyBranch) return;

		setIsLoading(true);

		try {
			// Create updated view1 object with the modified field
			const updatedView1 = {
				[editingField]: tempText,
			};
			// Make API call to update
			const url = `api/company/update/main?key_branch=${keyBranch}`;
			const payload = {
				method: "PUT",
				body: JSON.stringify({ view1: updatedView1 }),
			};
			const response = await ApiService.fetchData(url, payload);
			if (response?.code === 200) {
				// Update local state
				setEditedTexts((prev) => ({
					...prev,
					[editingField]: tempText,
				}));
				toast.success(response?.message || "Actualizado correctamente");
			} else {
				toast.error(response?.message || "Error al actualizar");
			}
		} catch (error) {
			toast.error("Error al actualizar los datos");
		} finally {
			setIsLoading(false);
			setEditingField(null);
		}
	};
	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault(); // Prevent default behavior (newline in multiline TextField)
			handleSave();
		}
	};
	const handleCancel = () => {
		setEditingField(null);
	};

	// ----------------------------------------------
	// 2. Función para colorear texto entre *...*
	// ----------------------------------------------
	const parseTextWithBlue = (text) => {
		// Separa el texto por segmentos que coincidan con: *contenido*
		if (!text) return;
		const regex = /(\*[^*]+\*)/g;
		const parts = text.split(regex);

		return parts.map((part, index) => {
			// Si el fragmento inicia y termina con '*'
			if (part.match(/\*[^*]+\*/)) {
				// Quita los asteriscos
				const clean = part.replace(/\*/g, "");
				return (
					<span key={index} style={{ color: "var(--theme-company)" }}>
						{clean}
					</span>
				);
			}
			// Fragmento normal
			return <span key={index}>{part}</span>;
		});
	};

	return (
		<div
			className="Intro-jhimbo"
			style={{
				backgroundImage: `url(${fondo_intro_jhimbo})`,
				backgroundSize: "cover",
				backgroundRepeat: "no-repeat",
				backgroundPosition: "center",
			}}>
			<div className="presentacion">
				{/* ------------------------- */}
				{/*   Título unificado       */}
				{/* ------------------------- */}
				<div className="title-presentacion">
					{editingField === "title" ? (
						<Box>
							{/* Instrucción arriba */}
							<p style={{ fontSize: "14px", marginBottom: "5px" }}>
								Si deseas colocar un texto en azul ponlo ente (*)
							</p>

							<TextField
								fullWidth
								multiline
								slotProps={{
									htmlInput: {
										maxLength: 40,
									},
								}}
								rows={2} // Permitir 2 filas (para el salto de línea)
								value={tempText}
								onKeyDown={handleKeyDown}
								onChange={(e) => setTempText(e.target.value)}
								sx={{
									"& .MuiInputBase-root": {
										fontSize: "22px",
										fontWeight: 700,
									},
								}}
							/>
							<Box mt={1} display="flex" gap={1}>
								<Button variant="contained" color="primary" onClick={handleSave} disabled={isLoading}>
									{isLoading ? "Guardando..." : "Guardar"}
								</Button>
								<Button variant="outlined" onClick={handleCancel}>
									Cancelar
								</Button>
							</Box>
						</Box>
					) : (
						// Al mostrar, parseamos para colorear texto entre *...*
						<h2 onClick={() => handleEdit("title")} style={{ cursor: "pointer", whiteSpace: "pre-line" }}>
							{parseTextWithBlue(editedTexts.title)}
						</h2>
					)}
				</div>

				{/* ------------------------- */}
				{/*   Descripción            */}
				{/* ------------------------- */}
				<div className="text-presentacion">
					{editingField === "text" ? (
						<Box>
							<TextField
								fullWidth
								multiline
								onKeyDown={handleKeyDown}
								slotProps={{
									htmlInput: {
										maxLength: 450,
									},
								}}
								rows={4}
								value={tempText}
								onChange={(e) => setTempText(e.target.value)}
							/>
							<Box mt={1} display="flex" gap={1}>
								<Button variant="contained" color="primary" onClick={handleSave} disabled={isLoading}>
									{isLoading ? "Guardando..." : "Guardar"}
								</Button>
								<Button variant="outlined" onClick={handleCancel}>
									Cancelar
								</Button>
							</Box>
						</Box>
					) : (
						<p onClick={() => handleEdit("text")} style={{ cursor: "pointer", whiteSpace: "pre-line" }}>
							{editedTexts.text}
						</p>
					)}
				</div>

				<div className="slogan">
					<div className="subtitle-slogan">
						{editingField === "message_text" ? (
							<Box>
								<TextField
									fullWidth
									value={tempText}
									onKeyDown={handleKeyDown}
									slotProps={{
										htmlInput: {
											maxLength: 25,
										},
									}}
									onChange={(e) => setTempText(e.target.value)}
									sx={{
										"& input": {
											color: "#333",
											fontWeight: 600,
											fontSize: "14px",
										},
									}}
								/>
								<Box mt={1} display="flex" gap={1}>
									<Button
										variant="contained"
										color="primary"
										onClick={handleSave}
										disabled={isLoading}>
										{isLoading ? "Guardando..." : "Guardar"}
									</Button>
									<Button variant="outlined" onClick={handleCancel}>
										Cancelar
									</Button>
								</Box>
							</Box>
						) : (
							<span onClick={() => handleEdit("message_text")} style={{ cursor: "pointer" }}>
								{editedTexts.message_text}
							</span>
						)}
					</div>

					<div className="calidad-badge" style={{ backgroundColor: `var(--theme-company)` }}>
						<div className="calidad-icon-wrap">
							<VerifiedIcon className="check-icon" />
						</div>
						{editingField === "highlight_text" ? (
							<Box>
								<TextField
									fullWidth
									onKeyDown={handleKeyDown}
									value={tempText}
									slotProps={{
										htmlInput: {
											maxLength: 15,
										},
									}}
									onChange={(e) => setTempText(e.target.value)}
									sx={{
										"& input": {
											color: "#fff",
											fontWeight: 600,
											fontSize: "14px",
										},
									}}
								/>
								<Box mt={1} display="flex" gap={1}>
									<Button variant="contained" color="primary" onClick={handleSave}>
										Guardar
									</Button>
									<Button variant="outlined" sx={{ backgroundColor: "#fff" }} onClick={handleCancel}>
										Cancelar
									</Button>
								</Box>
							</Box>
						) : (
							<span onClick={() => handleEdit("highlight_text")} style={{ cursor: "pointer" }}>
								{editedTexts.highlight_text}
							</span>
						)}
					</div>
				</div>
			</div>

			{/* Imágenes a la derecha */}
			<div className="presentacion-images">
				<div className="hex-container">
					<div className="hex hex-city">
						<img src={jhimbo_intro_1} alt="imagen" />
					</div>
					<div className="hex hex-woman">
						<img src={jhimbo_intro_2} alt="Imagen Mujer" />
					</div>
					<div className="hex hex-plant">
						<img src={jhimbo_intro_3} alt="Imagen Planta" />
					</div>
					<div className="hex hex-blue" style={{ backgroundColor: `var(--theme-company)` }} />
				</div>
			</div>
		</div>
	);
};

export default HomePresentacionEdit;
